import { useEffect, useState } from 'react';
import {
  runSEOAudit,
  type SEOAuditResult,
} from '@trashlab/sitesapp/src/utils/seo-audit';
import env from '@trashlab/sitesapp/src/env';

/**
 * Development-only SEO Checker Component
 *
 * Displays an SEO score badge in the corner of the screen during development.
 * Click to expand and see detailed audit results.
 *
 * This component only renders in non-production environments (local, staging, test).
 * Uses env.isProd for reliable environment detection since import.meta.env.DEV
 * is not reliably replaced in Vite production builds.
 */
export const SEOChecker = () => {
  const [auditResult, setAuditResult] = useState<SEOAuditResult | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Only show in non-production environments (local, staging, test)
  // Use env.isProd for reliable detection - import.meta.env.DEV is unreliable in builds
  const isDev = !env.isProd;

  useEffect(() => {
    if (!isDev) return;

    // Run audit after page loads
    const runAudit = () => {
      // Wait for page to fully render
      setTimeout(() => {
        const result = runSEOAudit();
        setAuditResult(result);
      }, 1000);
    };

    runAudit();

    // Re-run audit on route changes
    const handleRouteChange = () => {
      setTimeout(runAudit, 500);
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, [isDev]);

  if (!isDev || !auditResult || !isVisible) {
    return null;
  }

  const gradeColors = {
    A: 'bg-green-500',
    B: 'bg-green-400',
    C: 'bg-yellow-500',
    D: 'bg-orange-500',
    F: 'bg-red-500',
  };

  const severityColors = {
    critical: 'text-red-600',
    warning: 'text-yellow-600',
    info: 'text-blue-600',
  };

  return (
    <div
      className="fixed top-4 left-4 z-[9999] font-sans"
      style={{ fontFamily: 'system-ui, sans-serif' }}
    >
      {/* Collapsed Badge */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className={`${gradeColors[auditResult.grade]} text-white px-4 py-2 rounded-full shadow-lg hover:scale-105 transition-transform cursor-pointer flex items-center gap-2`}
        >
          <span className="font-bold text-lg">{auditResult.grade}</span>
          <span className="text-sm opacity-90">{auditResult.percentage}%</span>
          <span className="text-xs">SEO</span>
        </button>
      )}

      {/* Expanded Panel */}
      {isExpanded && (
        <div className="bg-white rounded-lg shadow-2xl border border-gray-200 w-96 max-h-[80vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div
            className={`${gradeColors[auditResult.grade]} text-white p-4 flex items-center justify-between`}
          >
            <div>
              <div className="text-2xl font-bold">
                Grade: {auditResult.grade}
              </div>
              <div className="text-sm opacity-90">
                Score: {auditResult.score}/{auditResult.maxScore} (
                {auditResult.percentage}%)
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setIsExpanded(false)}
                className="text-white/80 hover:text-white p-1"
                title="Minimize"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <button
                onClick={() => setIsVisible(false)}
                className="text-white/80 hover:text-white p-1"
                title="Close"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1 p-4">
            {/* Categories */}
            <div className="space-y-4">
              {auditResult.categories.map((category) => {
                const catPercentage = Math.round(
                  (category.score / category.maxScore) * 100
                );
                return (
                  <div key={category.name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-700 text-sm">
                        {category.name}
                      </span>
                      <span className="text-sm text-gray-500">
                        {category.score}/{category.maxScore}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          catPercentage >= 80
                            ? 'bg-green-500'
                            : catPercentage >= 60
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                        }`}
                        style={{ width: `${catPercentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Issues */}
            {auditResult.issues.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-800 mb-2">Issues</h3>
                <div className="space-y-2">
                  {auditResult.issues.map((issue, i) => (
                    <div
                      key={i}
                      className={`text-sm ${severityColors[issue.severity]} bg-gray-50 p-2 rounded`}
                    >
                      <div className="font-medium">{issue.message}</div>
                      <div className="text-gray-600 text-xs mt-1">
                        {issue.recommendation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {auditResult.recommendations.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Recommendations
                </h3>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  {auditResult.recommendations.map((rec, i) => (
                    <li key={i}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-3 bg-gray-50">
            <button
              onClick={() => {
                const result = runSEOAudit();
                setAuditResult(result);
              }}
              className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              Re-run Audit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOChecker;
